import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Overline } from "../components/UI";
import { SEO } from "../components/SEO";
import { endpoints } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, ShieldCheck } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Ask a Pregnancy Question — public educational enquiry page.        */
/*  Reuses the existing /api/enquiries backend. No personal contact    */
/*  fields are required; a placeholder name label "Pregnancy Question" */
/*  is sent so the row is legible in the admin Enquiry Inbox.          */
/* ------------------------------------------------------------------ */

const ASKER_TYPES = [
  "Patient",
  "Family member or caregiver",
  "Doctor or healthcare professional",
  "Other",
];

const TOPICS = [
  "Understanding a test or scan",
  "Pregnancy complication",
  "Medical condition in pregnancy",
  "Medicine or treatment in pregnancy",
  "Preconception or recurrent pregnancy loss",
  "Labour, delivery or postpartum care",
  "Other",
];

const MAX_LEN = 1000;

export default function AskAQuestion() {
  const [asker, setAsker] = useState("");
  const [topic, setTopic] = useState("");
  const [question, setQuestion] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!asker) return toast.error("Please tell us who is asking.");
    if (!topic) return toast.error("Please select a topic.");
    if (!question.trim()) return toast.error("Please write your question.");
    if (!consent) return toast.error("Please confirm the educational-question consent.");

    setSubmitting(true);
    try {
      await endpoints.createEnquiry({
        // Persisted on the enquiry row; the admin Enquiry Inbox lists by `name`.
        // Using a clear label keeps this row scannable next to appointment/referral
        // enquiries without exposing any personal identifier.
        name: "Pregnancy Question",
        subject: `Pregnancy Question — ${topic}`,
        message: question.trim(),
        phone: "",
        organisation: "",
        // Extras (preserved by the backend's permissive EnquiryIn model)
        type: "question",
        source_page: "/ask-a-question",
        asker_type: asker,
        topic,
        consent_educational: true,
      });
      setSubmitted(true);
      toast.success("Thank you. Your question has been received for editorial review.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = MAX_LEN - question.length;

  return (
    <div data-testid="ask-a-question-page">
      <Toaster richColors position="top-right" />
      <SEO
        title="Ask a Pregnancy Question | Dr Tania Gurdip Singh"
        description="Submit a general question about pregnancy, test results, medical conditions or treatment for possible coverage in future Articles or Clinical Insights."
        canonical="/ask-a-question"
      />

      {/* Hero */}
      <section className="bg-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Overline>Ask a Pregnancy Question</Overline>
          <div className="h-px w-12 bg-prestige my-6" />
          <h1
            data-testid="askq-heading"
            className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-navy tracking-tight"
          >
            What would you like explained clearly?
          </h1>
          <p className="text-lg md:text-xl text-stone2 mt-6 leading-relaxed max-w-2xl">
            Share a general question about pregnancy, a diagnosis, a test result or a
            treatment. Suitable questions may be answered through future Articles or
            Clinical Insights.
          </p>
          <p
            data-testid="askq-privacy"
            className="text-[14px] text-stone2 mt-6 max-w-2xl leading-relaxed border-l-2 border-prestige/60 pl-4"
          >
            Please do not share names, reports, photographs, prescriptions or
            detailed medical history. Questions are not published automatically.
            This page is for general education and is not a personal medical
            consultation.
          </p>
        </div>
      </section>

      {/* Form */}
      <Section>
        <div className="max-w-3xl">
          {submitted ? (
            <div
              className="bg-white border border-prestige/40 p-8"
              data-testid="askq-success"
            >
              <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest text-prestige">
                <ShieldCheck size={14} strokeWidth={1.5} /> Received
              </div>
              <div className="font-serif text-3xl text-navy mt-3">Thank you.</div>
              <p className="text-stone2 mt-3 leading-relaxed">
                Thank you. Your question has been received for editorial review. It
                will not be answered individually or published automatically.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="askq-form" noValidate>
              {/* Who is asking */}
              <Field label="Who is asking?" required>
                <select
                  value={asker}
                  onChange={(e) => setAsker(e.target.value)}
                  className={inputCls}
                  data-testid="askq-asker"
                  required
                >
                  <option value="">Select one</option>
                  {ASKER_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>

              {/* Topic */}
              <Field label="Topic" required>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className={inputCls}
                  data-testid="askq-topic"
                  required
                >
                  <option value="">Select a topic</option>
                  {TOPICS.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>

              {/* Your question */}
              <Field label="Your question" required>
                <textarea
                  rows={6}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value.slice(0, MAX_LEN))}
                  maxLength={MAX_LEN}
                  className={inputCls + " resize-y"}
                  placeholder="Write one general question. Please do not include names, reports or detailed personal medical information."
                  data-testid="askq-question"
                  required
                />
                <div
                  className={
                    "mt-2 mono text-[11px] uppercase tracking-[0.18em] " +
                    (remaining < 50 ? "text-terracotta" : "text-prestige")
                  }
                  data-testid="askq-counter"
                  aria-live="polite"
                >
                  {question.length} / {MAX_LEN} characters
                </div>
              </Field>

              {/* Emergency warning — clearly above submit */}
              <div
                data-testid="askq-emergency"
                role="note"
                className="border-l-2 border-terracotta bg-terracotta/5 px-4 py-3 text-[13.5px] text-navy leading-relaxed"
              >
                <strong className="font-medium">
                  This page is not monitored for emergencies.
                </strong>{" "}
                If you have urgent symptoms or concerns about your pregnancy,
                please seek immediate medical care.
              </div>

              {/* Consent */}
              <label
                className="flex items-start gap-3 text-[14px] text-stone2 cursor-pointer leading-relaxed"
                data-testid="askq-consent-label"
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                  data-testid="askq-consent"
                  required
                />
                <span>
                  I understand that this is an educational question, not a personal
                  consultation, diagnosis or treatment recommendation. My question
                  may be edited and anonymised for future educational content.
                </span>
              </label>

              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="askq-submit"
                  className="bg-prestige hover:bg-prestige-soft text-navy rounded-full px-8 py-3 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting…" : "Submit your question"}
                </button>
              </div>
            </form>
          )}
        </div>
      </Section>

      {/* Explore answers already available */}
      <Section className="bg-surfacealt/60" data-testid="askq-explore">
        <div className="max-w-3xl">
          <Overline>Explore answers already available</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {[
            { to: "/conditions", label: "Browse Conditions" },
            { to: "/articles", label: "Read Articles" },
            { to: "/clinical-insights", label: "Explore Clinical Insights" },
          ].map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`askq-explore-${it.to.slice(1)}`}
              className="block bg-white border border-line p-6 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 group"
            >
              <div className="font-serif text-xl text-navy group-hover:underline underline-offset-4 flex items-center justify-between gap-3">
                <span>{it.label}</span>
                <ArrowUpRight size={16} strokeWidth={1.5} className="text-prestige" />
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* ---- small local helpers to match the Contact form styling ---- */

const inputCls =
  "w-full bg-white border border-line px-4 py-3 text-navy text-[15px] focus:outline-none focus:border-navy transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="mono text-[11px] uppercase tracking-[0.22em] text-prestige mb-2">
        {label}
        {required && <span className="ml-1" aria-hidden="true">*</span>}
      </div>
      {children}
    </label>
  );
}
